import * as THREE from "three";
import { useRef, useMemo } from "react";
import { useTexture } from "@react-three/drei";
import { ThreeElements, useFrame, useThree } from "@react-three/fiber";

export function PhotoCube({
    projectId,
    texturePath,
    scale = 1.0,
    ...props
}: {
    projectId: string;
    texturePath: string;
    scale?: number;
} & ThreeElements["group"]) {

    const texture = useTexture("/images/" + texturePath);
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;

    const { gl, size } = useThree();

    const shaderRef = useRef<THREE.WebGLProgramParametersWithUniforms>(null);
    const groupRef = useRef<THREE.Group>(null);
    const htmlRef = useRef<HTMLDivElement>(null);

    const imageAspect = useMemo(() => {
        if (!texture.image) return 1;
        // @ts-ignore
        return texture.image.width / texture.image.height;
    }, [texture]);

    const customMaterial = useMemo(() => {
        const mat = new THREE.MeshStandardMaterial({
            color: "white",
            roughness: 1.0,
            metalness: 0.0,
            side: 2,
        });

        mat.onBeforeCompile = (shader) => {
            shader.uniforms.screenTex = { value: texture };
            shader.uniforms.resolution = {
                value: new THREE.Vector2(size.width, size.height),
            };
            shader.uniforms.scale = { value: scale };
            shader.uniforms.imageAspect = { value: imageAspect };

            // @ts-ignore
            shaderRef.current = shader;

            shader.vertexShader = `
                varying vec4 vClipCenter;
                ${shader.vertexShader}
            `;

            // inject shader stuff
            shader.vertexShader = shader.vertexShader.replace(
                "#include <project_vertex>",
                `
                #include <project_vertex>
                vClipCenter = projectionMatrix * modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);
                `,
            );

            shader.fragmentShader = `
                uniform sampler2D screenTex;
                uniform vec2 resolution;
                uniform float scale;
                uniform float imageAspect;
                varying vec4 vClipCenter;
                ${shader.fragmentShader}
            `;

            shader.fragmentShader = shader.fragmentShader.replace(
                "#include <map_fragment>",
                `
                vec2 ndcCenter = vClipCenter.xy / vClipCenter.w;
                vec2 uvCenter = ndcCenter * 0.5 + 0.5;
                vec2 uvScreen = gl_FragCoord.xy / resolution;

                vec2 uv = uvScreen - uvCenter;
                uv.x *= resolution.x / resolution.y;
                uv *= vClipCenter.w; // Perspective correction
                uv.y *= imageAspect;
                uv /= scale * 2.0;
                uv += 0.5;

                if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) discard;

                float dist = vClipCenter.w;
                float brightness = smoothstep(2.0, 5.0, dist);

                vec4 projectedColor = texture2D(screenTex, uv);
                // projectedColor.a = brightness;
                projectedColor.rgb *= brightness * 1.5;

                // https://gist.github.com/Reedbeta/e8d3817e3f64bba7104b8fafd62906df
                vec3 rgb = projectedColor.rgb;
                projectedColor.rgb = mix(pow((rgb + 0.055) * (1.0 / 1.055), vec3(2.4)),
                    rgb * (1.0/12.92),
                    lessThanEqual(rgb, vec3(0.04045)));


                diffuseColor *= projectedColor;
                `,
            );

            shader.fragmentShader = shader.fragmentShader.replace(
                "#include <dithering_fragment>",
                `
                #include <dithering_fragment>

                float finalDist = vClipCenter.w;
                float finalBrightness = smoothstep(2.0, 6.0, finalDist);
                finalBrightness = pow(finalBrightness, 0.5);

                // MULTIPLY THE FINAL OUTPUT COLOR
                // This takes whatever lighting/reflections Three.js calculated
                // and fades them out, ensuring the cube is truly invisible/black.
                gl_FragColor.rgb *= finalBrightness;
                
                // gl_FragColor.a *= finalBrightness; 
                `,
            );
        };

        return mat;
    }, [texture, scale]);

    useFrame(() => {
        if (shaderRef.current) {
            const pixelRatio = gl.getPixelRatio();
            shaderRef.current.uniforms.resolution.value.set(
                size.width * pixelRatio,
                size.height * pixelRatio,
            );
        }

        if (groupRef.current) {
            const worldPos = new THREE.Vector3();
            groupRef.current.getWorldPosition(worldPos);
        }
    });

    return (
        <group ref={groupRef} {...props}>
            <mesh
                geometry={new THREE.BoxGeometry(1, 1, 1)}
                material={customMaterial}
                scale={0.9}
                castShadow
            />
        </group>
    );
}
